import { getCoordinate } from '@/parsers/f2f/common';
import { parseGeneralListOfNearbyPharmacy } from '@/parsers/pharmacy';
import { PharmacyPickupService } from '@/services/pharmacy/PharmacyPickupService';
import { DiagnosisType } from '@/types/bff/gql-schema';
import { PharmacySearchQueries } from '@/types/pharmacy';

import { useFindGeneralListOfNearbyPharmacyV3ByUserQuery } from './pharmacy.gql.impl';

export const getSearchCriteria = (
  diagnosisType?: DiagnosisType | null,
  diagnosisTypeId?: string | null,
  treatmentMedicineId?: string | null,
  dispensationUnit?: number | null
) => {
  if (!diagnosisType) {
    return {};
  }

  if (!diagnosisTypeId) {
    return {};
  }

  if (diagnosisType === DiagnosisType.Treatment) {
    return {
      medicineSearchCriteria: {
        treatmentId: diagnosisTypeId,
        treatmentMedicineId: treatmentMedicineId,
        dispensationUnit: dispensationUnit,
      },
    };
  }

  if (diagnosisType === DiagnosisType.Department) {
    return {
      departmentSearchCriteria: {
        departmentId: diagnosisTypeId,
      },
    };
  }

  return {};
};

interface QueryRequestParams
  extends Pick<
    PharmacySearchQueries,
    'northEastLatitude' | 'northEastLongitude' | 'southWestLatitude' | 'southWestLongitude' | 'latitude' | 'longitude'
  > {
  diagnosisType?: DiagnosisType | null;
  diagnosisTypeId?: string | null;
  treatmentMedicineId?: string | null;
  dispensationUnit?: number | null;
  shouldIncludeDiagnosisPrice: boolean;
}

const useFindGeneralListOfNearbyPharmacyV3ByUser = (queryRequest: QueryRequestParams) => {
  const {
    latitude,
    longitude,
    diagnosisType,
    diagnosisTypeId,
    treatmentMedicineId,
    dispensationUnit,
    shouldIncludeDiagnosisPrice,
    ...restQuery
  } = queryRequest;

  const isEnabled = () => {
    if (shouldIncludeDiagnosisPrice && !treatmentMedicineId && !dispensationUnit) {
      return false;
    }

    return (
      !!restQuery.northEastLatitude &&
      !!restQuery.northEastLongitude &&
      !!restQuery.southWestLatitude &&
      !!restQuery.southWestLongitude
    );
  };

  const mapCoordinate = PharmacyPickupService.getMapCoordinate(restQuery);
  const userCoordinate = getCoordinate(latitude, longitude);

  const { data, isFetching, isLoading } = useFindGeneralListOfNearbyPharmacyV3ByUserQuery(
    {
      request: {
        ...{
          userLocation: {
            latitude: userCoordinate.latitude,
            longitude: userCoordinate.longitude,
          },
          geoBound: {
            northEastLatitude: mapCoordinate.northEastLatitude,
            northEastLongitude: mapCoordinate.northEastLongitude,
            southWestLatitude: mapCoordinate.southWestLatitude,
            southWestLongitude: mapCoordinate.southWestLongitude,
          },
          page: 1,
        },
        ...getSearchCriteria(diagnosisType, diagnosisTypeId, treatmentMedicineId, dispensationUnit),
      },
      treatmentId: diagnosisTypeId,
      treatmentMedicineId: treatmentMedicineId,
      dispensationUnit: dispensationUnit,
      shouldIncludeCheapestMedicine: !!treatmentMedicineId && !!dispensationUnit,
      shouldIncludeDiagnosisPrice,
    },
    {
      select: parseGeneralListOfNearbyPharmacy,
      enabled: isEnabled(),
      keepPreviousData: true,
    }
  );

  return {
    isLoading: isFetching || isLoading,
    data: data ?? [],
  };
};
export default useFindGeneralListOfNearbyPharmacyV3ByUser;
